import type { ConfigType } from '@plone/registry';
import type { BlockConfigBase } from '@plone/types';
import { composeSchema } from '@plone/volto/helpers/';
import SolarSearchEdit from '../../components/Blocks/SolarSearch/Edit';
import SolarSearchView, {
  BlockVariation,
} from '../../components/Blocks/SolarSearch/View';
import DegreeProgramme from '../../components/Blocks/SolarSearch/DegreeProgramme';
import CourseView from '../../components/Blocks/SolarSearch/CourseView';
import NewsView from '../../components/Blocks/SolarSearch/NewsView';
import EventsView from '../../components/Blocks/SolarSearch/Eventsview';
import PersonView from '../../components/Blocks/SolarSearch/Personview';
import { ContactsView, ContactsEdit } from '../../components/Blocks/Contacts';
import {
  LecturersView,
  LecturersEdit,
} from '../../components/Blocks/Lecturers';
import {
  SpecializationsView,
  SpecializationsEdit,
} from '../../components/Blocks/Specializations';
import {
  ResearchFocusView,
  ResearchFocusEdit,
} from '../../components/Blocks/ResearchFocus';
import {
  DistributorView,
  DistributorEdit,
} from '../../components/Blocks/Distributor';
import {
  SubTopicsView,
  SubTopicsEdit,
} from '../../components/Blocks/SubTopics';
import { ModulesView, ModulesEdit } from '../../components/Blocks/Modules';
import { teaserBlockVariations } from '../teasers';
import { listingBlockVariations } from '../listings';
import {
  gridTeaserDisableStylingSchema,
  teaserSchemaEnhancer,
} from '../../components/Blocks/Teaser/schema';

// Import icons directly from Volto
import descriptionSVG from '@plone/volto/icons/description.svg';
import contentListingSVG from '@plone/volto/icons/content-listing.svg';
import groupSVG from '@plone/volto/icons/group.svg';
import accessibilitySVG from '@plone/volto/icons/accessibility.svg';
import verifiedSVG from '@plone/volto/icons/verified.svg';
import targetSVG from '@plone/volto/icons/target.svg';
import menuAltSVG from '@plone/volto/icons/menu-alt.svg';
import tagSVG from '@plone/volto/icons/tag.svg';
import chunkSVG from '@plone/volto/icons/chunk.svg';

/**
 * Configure listing and search blocks
 * Follows SOLID principles by grouping related block configurations
 */
export const configureListingBlocks = (config: ConfigType) => {
  // Teaser block configuration
  config.blocks.blocksConfig.teaser = {
    ...config.blocks.blocksConfig.teaser,
    group: 'listingsearch',
    variations: [...teaserBlockVariations],
    imageScale: 'huge',
    schemaEnhancer: composeSchema(
      gridTeaserDisableStylingSchema,
      teaserSchemaEnhancer,
    ),
  } as BlockConfigBase & { imageScale: string };

  // Listing block configuration
  config.blocks.blocksConfig.listing = {
    ...config.blocks.blocksConfig.listing,
    variations: [...listingBlockVariations],
    icon: contentListingSVG,
    group: 'listingsearch',
  };

  // Search block configuration
  config.blocks.blocksConfig.search = {
    ...config.blocks.blocksConfig.search,
    title: 'Suche (Intern)',
    restricted: ({ user }) => {
      return !user?.roles?.includes('EditorAdmin');
    },
  };

  // Solar Search block configuration
  config.blocks.blocksConfig.solarSearch = {
    id: 'solarSearch',
    title: 'Search',
    icon: descriptionSVG,
    group: 'listingsearch',
    view: SolarSearchView,
    edit: SolarSearchEdit,
    mostUsed: false,
    sidebarTab: 1,
    variations: [
      {
        id: 'degree-programmes',
        isDefault: true,
        title: 'Degree Programmes',
        template: DegreeProgramme,
      },
      {
        id: 'course',
        title: 'Continuing Education',
        template: CourseView,
      },
      {
        id: 'news',
        title: 'News',
        template: NewsView,
      },
      {
        id: 'events',
        title: 'Events',
        template: EventsView,
      },
      {
        id: 'persons',
        title: 'Persons',
        template: PersonView,
      },
    ],
  } as BlockConfigBase & { variations: BlockVariation[] };

  // Contacts block configuration
  config.blocks.blocksConfig.contacts = {
    id: 'contacts',
    title: 'Kontakte',
    icon: groupSVG,
    group: 'listingsearch',
    view: ContactsView,
    edit: ContactsEdit,
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
  };

  // Lecturers block configuration
  config.blocks.blocksConfig.lecturers = {
    id: 'lecturers',
    title: 'Dozierende / Avatar',
    icon: accessibilitySVG,
    group: 'listingsearch',
    view: LecturersView,
    edit: LecturersEdit,
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
  };

  // Specializations block configuration
  config.blocks.blocksConfig.specializations = {
    id: 'specializations',
    title: 'Vertiefungsrichtungen',
    icon: verifiedSVG,
    group: 'listingsearch',
    view: SpecializationsView,
    edit: SpecializationsEdit,
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
  };

  // Research focus block configuration
  config.blocks.blocksConfig.research_focus = {
    id: 'research_focus',
    title: 'Forschungsschwerpunkte',
    icon: targetSVG,
    group: 'listingsearch',
    view: ResearchFocusView,
    edit: ResearchFocusEdit,
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
  };

  // Distributor block configuration
  config.blocks.blocksConfig.distributor = {
    id: 'distributor',
    title: 'Seitenlinks Verteiler',
    icon: menuAltSVG,
    group: 'listingsearch',
    view: DistributorView,
    edit: DistributorEdit,
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
  };

  // Sub topics block configuration
  config.blocks.blocksConfig.sub_topics = {
    id: 'sub_topics',
    title: 'Subthemen',
    icon: tagSVG,
    group: 'listingsearch',
    view: SubTopicsView,
    edit: SubTopicsEdit,
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
  };

  // Modules block configuration
  config.blocks.blocksConfig.modules = {
    id: 'modules',
    title: 'Programmaufbau',
    icon: chunkSVG,
    group: 'listingsearch',
    view: ModulesView,
    edit: ModulesEdit,
    restricted: false,
    mostUsed: false,
    sidebarTab: 1,
  };

  return config;
};
